import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, map, merge, mergeAll, mergeMap, of, startWith, switchMap, take, takeWhile, tap, toArray } from 'rxjs'
import { FirebaseError } from '@angular/fire/app'
import { Firestore, doc, docData, where } from '@angular/fire/firestore'
import { log } from '@nx-superprep/utils'
import { isEmpty, isEqual, partition } from 'lodash-es'

import {
  CategoryTemplate, GroupTemplate, LevelTemplate, ModuleTemplate, CardTemplate,
  SubjectTemplate, ConceptTemplate, Topic, PopDataType, LibrarySection,
  LibrarySectionItem, ModuleStats, ConceptStats
} from '../models/back-end'
import { Concept, LibraryNavigation, User } from '../models/front-end'
import { LongtailService } from './longtail.service'
import { QueryOptions } from "./query-options"
import { getCollection } from "./get-collection"

@Injectable({providedIn: 'root'})
export class LibraryService extends LongtailService {
  private readonly _subjectOrder = ['French', 'Spanish', 'Chinese', 'Inglés', 'Latin', 'Geography', 'North America', 'World Flags', 'Biology', 'Chemistry', 'Biotechnology', 'Ecology', 'Nursing']

  private readonly _categories = new Map<string, CategoryTemplate>()
  private readonly _subjects = new Map<string, SubjectTemplate>()
  private readonly _groups = new Map<string, GroupTemplate>()
  private readonly _levels = new Map<string, LevelTemplate>()
  private readonly _modules = new Map<string, ModuleTemplate>()
  private readonly _cards = new Map<string, CardTemplate>()
  private readonly _concepts = new Map<string, ConceptTemplate>()
  private readonly _conceptStats = new Map<string, Concept[]>()
  private readonly _topics = new Map<string, Topic>()
  private readonly _sections = new Map<string, LibrarySection>()

  private _navigationState: LibraryNavigation | undefined
  private _cachedLibrarySections: LibrarySection[] = []
  private _librarySections: LibrarySection[] = []
  private _librarySectionsLoading = new BehaviorSubject<boolean>(false)
  private _librarySectionsSubject = new BehaviorSubject<LibrarySection[]>([])

  readonly firestore = inject(Firestore)

  constructor() {
    super()
    this.getStudyLibrarySections().pipe(take(1)).subscribe(result => log.debug(result))
  }

  // Getters and setters
  get navigationState() { return this._navigationState; }
  set navigationState(value) {
    this._navigationState = value ? {...value, selection: {...value.selection, didChange: true}} : undefined
    if (value?.selection) this.logLibraryEvent(value)
  }

  get currentLesson() { return this._navigationState?.selection?.detail; }
  get categories() { return [...this._categories.values()]; }
  get subjects() { return [...this._subjects.values()]; }
  get groups() { return [...this._groups.values()]; }
  get levels() { return [...this._levels.values()]; }
  get modules() { return [...this._modules.values()]; }
  get cards() { return [...this._cards.values()]; }
  get concepts() { return [...this._concepts.values()]; }
  get topics() { return [...this._topics.values()]; }
  get sections() { return Array.from(this._sections.values()); }
  get librarySections() { return this.backendService.applicationSkin === 'ASL' ? [] : this._librarySections; }
  private set librarySections(value: LibrarySection[]) { this._librarySections = this.sortLibrarySections(value) }
  get cachedLibrarySections() { return this.backendService.applicationSkin === 'ASL' ? [] : this._cachedLibrarySections }
  private set cachedLibrarySections(sections: LibrarySection[]) { this._cachedLibrarySections = this.sortLibrarySections(sections) }

  get studyLibrarySections() {
    return this._librarySectionsSubject.asObservable().pipe(
      startWith(this.cachedLibrarySections), // Sort cached data before emitting
      distinctUntilChanged((a, b) => isEqual(a, b))
    )
  }

  get librarySectionsLoading$() {
    return this._librarySectionsLoading.asObservable()
  }

  clear() {
    [
      this._categories, this._subjects, this._groups, this._levels,
      this._modules, this._cards, this._concepts,
      this._conceptStats, this._topics, this._sections,
    ].forEach(map => map?.clear())
    this._navigationState = undefined
    this._librarySections = []
    this._cachedLibrarySections = []
  }

  override onUserChange(user: User | undefined) {
    this._navigationState = undefined
    return super.onUserChange(user).pipe(
      switchMap(user =>
        user ? this.getStudyLibrarySections().pipe(map(() => user)) : of(user)
      )
    )
  }

  private replaceLessonWithUnit(text: string | undefined): string | undefined {
    if (!text) return undefined
    return text.replace(/\blesson?/gi, match => {
      const isCapitalized = match[0] === match[0].toUpperCase()
      const isPlural = match.toLowerCase().endsWith('s')
      return isCapitalized ? (isPlural ? 'Units' : 'Unit') : (isPlural ? 'units' : 'unit')
    })
  }

  sortLibrarySections(sections: LibrarySection[]): LibrarySection[] {
    // Ensure we have 'Subjects' and 'Your custom units' sections
    const requiredSections = [
      sections.find(s => s.title === 'Subjects'),
      sections.find(s => s.title === 'Your custom units')
    ].filter(Boolean) as LibrarySection[];

    // Get other sections excluding the required ones
    const otherSections = sections.filter(
      s => s.title !== 'Subjects' && s.title !== 'Your custom units'
    );

    // Combine them with required sections first
    const combined = [...requiredSections, ...otherSections];

    // Process each section's internal data
    return combined.map((section) => {
      // Sort subjects if this is the Subjects section
      if (section.title === 'Subjects' && section.items) {
        return {
          ...section,
          items: [...section.items].sort((a, b) => this.compareSubjectName(a.title, b.title)),
          title: this.replaceLessonWithUnit(section.title),
          subTitle: this.replaceLessonWithUnit(section.subTitle)
        };
      }
      return {
        ...section,
        title: this.replaceLessonWithUnit(section.title),
        subTitle: section.title === 'Your custom units' && isEmpty(section.subTitle) ?
                  'Continue your learning' : this.replaceLessonWithUnit(section.subTitle)
      };
    });
  }

  // Comparison methods
  compareSubjectName(a: string, b: string): number {
    const aIndex = this._subjectOrder.indexOf(a)
    const bIndex = this._subjectOrder.indexOf(b)
    return aIndex === -1 ? (bIndex !== -1 ? 1 : 0) : bIndex === -1 ? -1 : aIndex - bIndex
  }

  compare<T extends { id?: string }>(a: T, b: T, ids: string[], asc = true) {
    return (a.id && b.id && ids.indexOf(a.id) > ids.indexOf(b.id) ? 1 : -1) * (asc ? 1 : -1)
  }

  compareSections(a: LibrarySection, b: LibrarySection) {
    const order = ['Subjects', 'Your custom units', ...this.librarySections
      .map(s => s.title)
      .filter(t => t !== 'Subjects' && t !== 'Your custom units')]

    return order.indexOf(a?.title) - order.indexOf(b?.title)
  }

  // Data fetching methods
  getCollection<T extends {id?: string|undefined}>(collection: string, options: QueryOptions) { return getCollection<T>(this.firestore, collection, options, this.lt_application) }

  getStudyLibrarySections = () => {
    if (!this.uid) { log.debug('no active user'); return of([]); }
    // Show loading state
    this._librarySectionsLoading.next(true);
    return this.studyGetLibrary({application: this.lt_application}).pipe(
      catchError((val: FirebaseError) => {
        log.debug(val.stack)
        return of({sections: this._cachedLibrarySections})
      }),
      tap((result) => {
        // Use the setter which will apply sorting
        this.cachedLibrarySections = result.sections;
        this._librarySectionsSubject.next(this._cachedLibrarySections);
        this.librarySections = this._cachedLibrarySections;
        this._librarySectionsLoading.next(false);
      }),
      map(() => this._cachedLibrarySections)
    )
  }

  getAll() {
    return combineLatest([this.getCategories(), this.getSubjects(), this.getGroups(), this.getLevels(), this.getModules(), this.getConcepts()])
  }

  // updaters

  getCategories(names?: string|string[]) {
    return this.getCollection<CategoryTemplate>(CategoryTemplate.collectionPath(), { names, application:this.lt_application, cache:this._categories })
  }

  getSubjects(names?: string|string[]) {
    return this.getCollection<SubjectTemplate>(SubjectTemplate.collectionPath(), { names, application:this.lt_application, cache:this._subjects })
  }

  getGroups(names?: string|string[]) {
    return this.getCollection<GroupTemplate>(GroupTemplate.collectionPath(), { names, application:this.lt_application, cache:this._groups })
  }

  getLevels(names?: string|string[]) {
    return this.getCollection<LevelTemplate>(LevelTemplate.collectionPath(), { names, application:this.lt_application, cache:this._levels })
  }

  getModules(names?: string|string[]) {
    return this.getCollection<ModuleTemplate>(ModuleTemplate.collectionPath(), { names, application:this.lt_application, cache:this._modules })
  }

  getCards(names?: string|string[]) {
    return this.getCollection<CardTemplate>(CardTemplate.collectionPath(), { names, application:this.lt_application, cache:this._cards })
  }

  // categories

  getCategoryForSubject(id: string) {
    return this.getCollection<CategoryTemplate>(CategoryTemplate.collectionPath(), {filters: id ? [where('subjectIds', 'array-contains', id)] : [], application:this.lt_application, cache:this._categories})
  }

  // Levels

  getLevelForSubject(id: string) {
    return this.getCollection<LevelTemplate>(LevelTemplate.collectionPath(), {filters: id ? [where('subjects', 'array-contains', {id:id.toUpperCase(), title:id.toTitleCase()})] : [], application:this.lt_application, cache:this._levels})
  }

  getLevelForGroup(id: string) {
    return this.getCollection<LevelTemplate>(LevelTemplate.collectionPath(), {filters: id ? [where('parentGroupIds', 'array-contains', id.toUpperCase())] : [], application:this.lt_application, cache:this._levels})
  }

  // Groups

  getGroupForSubject(id: string) {
    return this.getCollection<GroupTemplate>(GroupTemplate.collectionPath(), {filters: id ? [where('parentSubjectIds', 'array-contains', id.toUpperCase())] : [], application:this.lt_application, cache:this._groups})
  }

  //

  __getLevelForSubject(id: string) {
    return this.getGroupForSubject(id)
    .pipe(
      catchError((val: FirebaseError) => { log.debug(val.stack, val.message); return [] }),
      mergeAll(),
      tap(t => log.debug('1',t)),
      map(g => g.id ? this.getLevelForGroup(g.id) : of([])),
      tap(t => log.debug('2',t)),
      mergeAll(),
      tap(t => log.debug('3',t)),
    )
  }
  _getLevelForSubject(id: string) {
    return this.getGroupForSubject(id)
    .pipe(
      mergeAll(),
      map(group => (group.id ? this.getLevelForGroup(group.id) : of([]))
        .pipe(
          mergeAll(),
          map(level => (level.id ? this.getModuleForLevel(level.id) : of([]))
          .pipe(
            mergeAll(),
            map(lesson => lesson),
          ))
        )
      ),
      map(t => t),
      mergeAll(),
      map(t => t),
      tap({
        next: (t) => log.debug('[next] Called', t),
        error: (t) => log.debug('[error] Not called', t),
        complete: () => log.debug('[tap complete] Not called')
      }),
      catchError((val: FirebaseError) => { log.debug(val.stack, val.message); return [] })
    )
  }

  // Modules

  getModuleForLevel(id: string) {
    return this.getCollection<ModuleTemplate>(ModuleTemplate.collectionPath(), {filters: id ? [where(`parentLevelIds`, 'array-contains', id.toUpperCase())] : [], cache:this._modules})
  }

  getModuleForSubject(id: string) {
    return this.getCollection<ModuleTemplate>(ModuleTemplate.collectionPath(), {filters: id ? [where(`subjects`, 'array-contains', {id:id.toUpperCase(), title:id.toTitleCase()})] : [], cache:this._modules})
  }

  getModuleStats(moduleId: string, uid = this.uid) {
    if (!uid) { log.debug('no active user'); return of() }
    const ref = doc(this.firestore, `${ModuleStats.collectionPath(uid)}/${moduleId}`)
    return docData(ref)
    .pipe(
      catchError((val: FirebaseError) => { log.debug(val.stack); return [] }),
      map(c => c ? ({ moduleId: c['id'], ...c } as ModuleStats) : undefined )
    )
  }

  // concepts

  getConceptStats(id: string) {
    return merge(of(this._conceptStats.get(id)??[]), this.getConceptStatForModuleId(id))
  }

  getConcepts(id?: string) {
    return this.getCollection<ConceptTemplate>(ConceptTemplate.collectionPath(), {filters: id ? [where(`moduleId`, '==', id)] : [], cache:this._concepts})
  }

  getConceptStatForModuleId(id: string, uid = this.uid) {
    if (!uid) { log.debug('no active user'); return of([]) }
    return this.getConcepts(id)
    .pipe(
      catchError((val: FirebaseError) => { log.debug(val.stack, id); return [] }),
      mergeMap(items => items.length === 0 ? [] : items.map((item,index) => ({item, index, count:items.length}))),
      mergeMap(({item, index, count}) => docData(doc(this.firestore, `${ConceptStats.collectionPath(uid, id)}/${item.id}`))
        .pipe(
          catchError((val: FirebaseError) => { log.debug(val.stack, item); return [] }),
          map(s => ({item:{...item, ...s}, index, count}))
        )
      ),
      takeWhile((value) => value.index + 1 < value.count, true),
      map((value) => value.item as Concept),
      toArray(),
      tap(c => this._conceptStats.set(id, c.sort((a,b) => a.title.localeCompare(b.title))) )
    )
  }

  // subject groups ids

 groupIdsForSubject(name: string) {
    return name
    ? this.getSubjects(name.toUpperCase()).pipe(mergeMap(ss => ss.map(s => s.groupIds)))
    : of([] as string[])
  }

  // templates for section

  getTemplatesForDataType(dataType: PopDataType) {
    switch(dataType) {
      case PopDataType.CategoryTemplate: return this.getCategories()
      case PopDataType.SubjectTemplate: return this.getSubjects()
      case PopDataType.GroupTemplate: return this.getGroups()
      case PopDataType.LevelTemplate: return this.getLevels()
      case PopDataType.ModuleTemplate: return this.getModules()
      case PopDataType.CardTemplate: return this.getCards()
      default: return of([])
    }
  }

  isSubsetOf(items: string[], id: string|undefined) {
    return id && items?.includes(id)
  }

  // by parent

  subjectsForCategory(item: CategoryTemplate) {
    return this.subjects.filter(s => this.isSubsetOf(item?.subjectIds, s.id))
  }

  groupsForSubject(item: SubjectTemplate) {
    return this.groups.filter(s => this.isSubsetOf(item?.groupIds, s.id))
  }

  levelsForGroup(item: GroupTemplate) {
    return partition(this.levels.filter(s => this.isSubsetOf(item?.levelIds, s.id)).sort((a,b) => this.compare(a, b, item.levelIds)), (a) => (!a.id.endsWith('_TEST'))).flat()
  }

  modulesForLevel(item: LevelTemplate) {
    return this.modules.filter(s => this.isSubsetOf(item?.moduleIds, s.id)).sort((a,b) => this.compare(a, b, item.moduleIds))
  }

  // by topic

  levelsForTopic(item: Topic) {
    return item.levelId ? this.levels.filter(c => c.id === item.levelId) : []
  }

  groupsForTopic(item: Topic) {
    return item.levelId ? this.groups.filter(c => c.levelIds.includes(item.levelId)) : []
  }

  // by path

  private levelsForPath(path: {subject: string, subset: string}) {
    return this.levels.filter(s => s.title === path.subset && s.subjects.find(s => s.title === path.subject))
  }

  private modulesForPath(path: {subject: string, subset: string}) {
    return this.modules.filter(s => s.title === path.subset && s.subjects.find(s => s.title === path.subject))
  }

  // fetchers

  getModulesForLibrarySectionItem(item: LibrarySectionItem) {
    return this.getModules('moduleIds' in item ? item.moduleIds as string[] : item.itemId as string)
  }

  getModulesForLevel(item: LevelTemplate) {
    return 'moduleIds' in item ? this.getModules(item.moduleIds as string[]) : of(this.modulesForLevel(item))
  }

  getLibrarySubjects() {
    const name = this.backendService.applicationSkin === 'ASL' ? 'ASL' : undefined
    return (name ? this.getSubjects().pipe(map(s => s.filter(s => (s.id ?? '').startsWith(name)))) : this.getSubjects())
    .pipe(map(s => s.sort((a, b) => this.compareSubjectName(a.title, b.title))))
  }

  // concepts

  conceptForModuleId(id: string) {
    return id ? this.concepts.filter(c => c.moduleId === id) : []
  }
  conceptForModule(item: ModuleTemplate) {
    return item.id ? this.concepts.filter(c => c.moduleId === item.id) : []
  }

  // sections

  getItemsForSection(section?: LibrarySection) {
    if (!section?.items) { return of([]) }
    const dataType = section.items[0].dataType
    if (!dataType) { return of([]) }
    return this.getTemplatesForDataType(dataType)
  }

  getLibrarySections() {
    return merge(this.librarySections, this.studyLibrarySections)
  }

  retrieveSubjectSection() {
    return this.getLibrarySubjects()
    .pipe(
      mergeMap(items => items.length === 0 ? [] : items.map((s, i) => ({item:s, index: i, count: items.length}))),
      mergeMap(({item, index, count}) =>
        of({
          value: {
            itemId: item.id,
            title: item.title,
            dataType: PopDataType.SubjectTemplate,
            iconName: item.imageString,
            appAvailability: item.appAvailability,
            item
          } as LibrarySectionItem,
          index,
          count
        })
      ),
      takeWhile((item) => ((item.index + 1) < item.count), true),
      map(item => item.value),
      toArray(),
      map(items => ({ title: "Subjects", items } as LibrarySection)),
      tap(section => { this._sections.set('Subjects', section)})
    )
  }

  retrieveModuleSection(s: { title: string; subTitle: string; items: string[]; }) {
    return this.getModules(s.items)
    .pipe(
      mergeMap(items => items.length === 0 ? [] : items.map((s, i) => ({item:s, index: i, count: items.length}))),
      mergeMap(({item, index, count}) =>
        of({
          value: {
            title: item.title,
            itemId: item.id,
            dataType: PopDataType.ModuleTemplate,
            iconName: item.imageString,
            appAvailability: item.appAvailability,
            item
          } as LibrarySectionItem,
          index,
          count
        })
      ),
      takeWhile((item) => ((item.index + 1) < item.count), true),
      map(item => item.value),
      toArray(),
      map(items => ({ title:s.title, subTitle:s.subTitle, items } as LibrarySection)),
      tap(section => this._sections.set(s.title??'', section))
    )
  }

  getContentForSubject(subject: string)  {
    return combineLatest([
      this.getGroupForSubject(subject),
      this.getLevelForSubject(subject),
      this.getModuleForSubject(subject),
    ]).pipe(
      distinctUntilChanged(isEqual)
    )
  }
}
